import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { useUsers, ITEMS_PER_PAGE } from './hooks/useUsers';
import {
  UserFilters,
  UsersTable,
  UsersPagination,
  EditUserDialog,
  ViewUserDialog,
  GrantPremiumDialog,
} from './components';
import { UsersPageProps } from './types';

export default function UsersPage({ isDark }: UsersPageProps) {
  const {
    users,
    total,
    totalPages,
    currentPage,
    isLoading,
    searchQuery,
    roleFilter,
    subscriptionFilter,
    isEditDialogOpen,
    editFormData,
    viewingUser,
    isViewDialogOpen,
    isLoadingDetails,
    premiumUser,
    isPremiumDialogOpen,
    isPremiumSaving,
    setCurrentPage,
    handleSearch,
    handleRoleFilter,
    handleSubscriptionFilter,
    handleEdit,
    handleView,
    handleSave,
    handleOpenPremium,
    handleGrantPremium,
    handleRevokePremium,
    setIsEditDialogOpen,
    setIsViewDialogOpen,
    setIsPremiumDialogOpen,
    setEditFormData,
  } = useUsers();

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className={cn(
          'text-2xl lg:text-3xl font-bold tracking-tight',
          isDark ? 'text-white' : 'text-[#0F172A]'
        )}>Users</h1>
        <p className={cn(
          'text-sm mt-1',
          isDark ? 'text-slate-400' : 'text-slate-500'
        )}>Manage all users and their data</p>
      </div>

      {/* Users Table Card */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark 
          ? 'bg-[#1E293B] shadow-lg shadow-black/10' 
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        {/* Header with Filters */}
        <div className={cn(
          'p-5 lg:p-6 border-b',
          isDark ? 'border-slate-700' : 'border-slate-200'
        )}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className={cn(
                'text-lg font-semibold',
                isDark ? 'text-white' : 'text-[#0F172A]'
              )}>All Users</h3>
              <p className={cn(
                'text-sm mt-1',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>
                {total} total users
              </p>
            </div>
            <UserFilters
              isDark={isDark}
              searchQuery={searchQuery}
              roleFilter={roleFilter}
              subscriptionFilter={subscriptionFilter}
              onSearch={handleSearch}
              onRoleFilter={handleRoleFilter}
              onSubscriptionFilter={handleSubscriptionFilter}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <UsersTable
            isDark={isDark}
            users={users}
            isLoading={isLoading}
            onView={handleView}
            onEdit={handleEdit}
            onPremium={handleOpenPremium}
            formatDate={formatDate}
          />
        </div>

        {/* Pagination */}
        {!isLoading && users.length > 0 && (
          <UsersPagination
            isDark={isDark}
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        )}
      </Card>

      {/* Dialogs */}
      <EditUserDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={editFormData}
        onFormDataChange={setEditFormData}
        onSave={handleSave}
      />

      <ViewUserDialog
        isDark={isDark}
        isOpen={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        user={viewingUser}
        isLoading={isLoadingDetails}
        formatDate={formatDate}
      />

      <GrantPremiumDialog
        isDark={isDark}
        isOpen={isPremiumDialogOpen}
        onOpenChange={setIsPremiumDialogOpen}
        user={premiumUser}
        isSaving={isPremiumSaving}
        onGrant={handleGrantPremium}
        onRevoke={handleRevokePremium}
      />
    </div>
  );
}
